import React, { useEffect, useState, useRef, useCallback } from "react";
import { usePage, Link, router } from "@inertiajs/react";
import { useTheme } from "next-themes";
import {
  MoonIcon,
  SunIcon,
  User as UserIcon,
  Trophy,
  LogOut,
  LogIn,
  UserPlus,
  Search,
  Menu,
  X,
  LayoutDashboard,
  Home,
  CalendarDays,
  Info,
  ScrollText,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { InstallPrompt } from "@/components/InstallPrompt";

import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import raceFlowLogo from "@/assets/images/race_flow.svg";
import defaultAvatar from "@/assets/images/default_avatar.png";

interface BaseLayoutProps {
  children: React.ReactNode;
}

interface SharedProps {
  auth: {
    current_driver: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      avatar_url: string | null;
    } | null;
    current_admin: {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
    } | null;
    current_organizer: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      organizer_logo_url: string | null;
    } | null;
  };
  flash: {
    notice: string | null;
    alert: string | null;
    error: string | null;
    success: string | null;
  };
  config: {
    vapid_public_key: string;
  };
  [key: string]: unknown;
}

const NAV_LINKS = [
  { href: "/", label: "Home", icon: Home },
  { href: "/events", label: "Events", icon: CalendarDays },
  { href: "/championships", label: "Championships", icon: Trophy },
  { href: "/clubs", label: "Clubs", icon: UserIcon },
];

const ROLE_COLORS: Record<string, string> = {
  driver: "bg-primary/10 text-primary border-primary/20",
  admin: "bg-destructive/10 text-destructive border-destructive/20",
  organizer: "bg-primary/10 text-primary border-primary/20",
};

const BaseLayout = ({ children }: BaseLayoutProps) => {
  const { auth, flash, ...pageProps } = usePage<SharedProps>().props;
  const url = (pageProps as { url?: string }).url || usePage().url;
  const { theme, setTheme, resolvedTheme } = useTheme();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [appBannerDismissed, setAppBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem("raceflow_app_banner_dismissed") === "true";
    } catch {
      return false;
    }
  });

  const accountRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Flash toasts
  useEffect(() => {
    if (flash.notice) toast.info(flash.notice);
    if (flash.alert) toast.warning(flash.alert);
    if (flash.error) toast.error(flash.error);
    if (flash.success) toast.success(flash.success);
  }, [flash.notice, flash.alert, flash.error, flash.success]);

  // Close account dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Lock scroll when overlays open
  useEffect(() => {
    document.body.style.overflow = searchOpen || menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [searchOpen, menuOpen]);

  // Focus search input when overlay opens
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  // Escape closes everything
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSearchOpen(false);
        setMenuOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close overlays on Inertia navigation
  useEffect(() => {
    return router.on("navigate", () => {
      setMenuOpen(false);
      setAccountOpen(false);
      setSearchOpen(false);
    });
  }, []);

  const handleLogout = (path: string) => {
    setAccountOpen(false);
    router.delete(path);
  };

  const toggleTheme = () => {
    setTheme((resolvedTheme || theme || "light") === "dark" ? "light" : "dark");
  };

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, []);

  const currentUser =
    auth.current_driver || auth.current_admin || auth.current_organizer;
  const userRole = auth.current_driver
    ? "driver"
    : auth.current_admin
      ? "admin"
      : auth.current_organizer
        ? "organizer"
        : null;

  const getUserName = () => {
    if (auth.current_driver)
      return `${auth.current_driver.first_name} ${auth.current_driver.last_name}`;
    if (auth.current_admin)
      return auth.current_admin.first_name
        ? `${auth.current_admin.first_name} ${auth.current_admin.last_name}`
        : auth.current_admin.email;
    if (auth.current_organizer)
      return `${auth.current_organizer.first_name} ${auth.current_organizer.last_name}`;
    return "";
  };

  const getAvatarUrl = () => {
    if (auth.current_driver?.avatar_url) return auth.current_driver.avatar_url;
    if (auth.current_organizer?.organizer_logo_url)
      return auth.current_organizer.organizer_logo_url;
    return null;
  };

  const getProfileLink = () => {
    if (auth.current_driver) return `/drivers/${auth.current_driver.id}`;
    if (auth.current_admin) return "/admin/dashboard";
    if (auth.current_organizer)
      return `/organizer/${auth.current_organizer.id}`;
    return "/";
  };

  const getLogoutPath = () => {
    if (auth.current_driver) return "/logout";
    if (auth.current_admin) return "/admin/logout";
    if (auth.current_organizer) return "/organizer/logout";
    return "/logout";
  };

  const isActive = (href: string) =>
    href === "/" ? url === "/" : url.startsWith(href);

  const avatarUrl = getAvatarUrl();

  const isDark = resolvedTheme === "dark";

  return (
    <div className="min-h-screen relative">
      {/* Gradient background */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{ opacity: isDark ? 0.15 : 0.3 }}>
        <BackgroundGradientAnimation
          gradientBackgroundStart={isDark ? "rgb(10, 10, 20)" : "rgb(245, 245, 250)"}
          gradientBackgroundEnd={isDark ? "rgb(5, 5, 15)" : "rgb(240, 240, 245)"}
          firstColor="247, 181, 0"
          secondColor="27, 59, 161"
          thirdColor="255, 0, 0"
          fourthColor="247, 181, 0"
          fifthColor="27, 59, 161"
          pointerColor="247, 181, 0"
          interactive={false}
          containerClassName="!absolute"
        />
      </div>
      <Toaster position="top-center" />
      <InstallPrompt />

      {/* ───── iOS App Banner ───── */}
      {!appBannerDismissed && (
        <div className="relative z-50 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 py-2 flex items-center justify-center gap-3 text-sm">
            <Smartphone className="h-4 w-4 shrink-0" />
            <span className="font-medium">
              RaceFlow is now on iOS!
            </span>
            <a
              href="https://apps.apple.com/in/app/raceflow/id6767195652"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-primary-foreground/20 px-3 py-0.5 text-xs font-semibold hover:bg-primary-foreground/30 transition-colors"
            >
              Download
            </a>
            <button
              onClick={() => {
                setAppBannerDismissed(true);
                try { localStorage.setItem("raceflow_app_banner_dismissed", "true"); } catch {}
              }}
              className="absolute right-4 p-1 rounded hover:bg-primary-foreground/20 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ───── Navbar ───── */}
      <div className="sticky top-0 z-50 px-4 pt-3 overflow-visible">
        <header className="container mx-auto relative overflow-visible">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              backgroundColor: isDark ? "rgba(20,20,30,0.4)" : "#f7f6f566",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid",
              borderColor: isDark ? "rgba(255,255,255,0.08)" : "#EFEEEE",
              transition: "background .2s ease, backdrop-filter .2s ease",
            }}
          />
          <nav className="relative overflow-visible px-6 flex items-center justify-between" style={{ height: 65 }}>
            {/* Logo */}
            <Link href="/" className="flex-shrink-0" onClick={closeAll}>
              <img
                src={raceFlowLogo}
                alt="RaceFlow"
                className="h-8 w-auto sm:h-10 md:h-12 drop-shadow-[0_1px_0.5px_rgba(0,0,0,0.4)]"
              />
            </Link>

            {/* Center Nav — desktop */}
            <ul className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-300",
                        isActive(link.href)
                          ? "text-foreground bg-accent"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                      )}
                    >
                      <Icon className="w-4 h-4" />
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Right actions */}
            <div className="flex items-center gap-1">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-300"
                aria-label="Search"
              >
                <Search className="h-[1.2rem] w-[1.2rem]" />
              </button>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-300"
                aria-label="Toggle theme"
              >
                <SunIcon className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <MoonIcon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </button>

              {/* Account */}
              <div ref={accountRef} className="relative">
                <button
                  onClick={() => setAccountOpen(!accountOpen)}
                  className={cn(
                    "inline-flex items-center justify-center h-9 w-9 rounded-md transition-colors duration-300",
                    "text-muted-foreground hover:text-foreground hover:bg-accent",
                    accountOpen && "bg-accent text-foreground"
                  )}
                  aria-label="Account"
                >
                  {currentUser && avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : currentUser ? (
                    <img
                      src={defaultAvatar}
                      alt=""
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <UserIcon className="h-[1.2rem] w-[1.2rem]" />
                  )}
                </button>

                {/* Dropdown */}
                <div
                  className={cn(
                    "absolute right-0 top-full mt-2 w-[min(24rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover shadow-lg transition-all duration-200 origin-top-right",
                    accountOpen
                      ? "opacity-100 scale-100 pointer-events-auto"
                      : "opacity-0 scale-95 pointer-events-none"
                  )}
                >
                  {currentUser ? (
                    <>
                      {/* User header */}
                      <Link
                        href={getProfileLink()}
                        onClick={closeAll}
                        className="flex items-center gap-4 p-6 rounded-t-xl hover:bg-accent/50 transition-colors group"
                      >
                        <img
                          src={avatarUrl || defaultAvatar}
                          alt=""
                          className="h-14 w-14 rounded-full object-cover flex-shrink-0 ring-2 ring-border group-hover:ring-primary/30 transition-all"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-lg font-semibold truncate group-hover:text-primary transition-colors">
                            {getUserName()}
                          </p>
                          <span
                            className={cn(
                              "inline-flex items-center px-1.5 py-px rounded text-[9px] font-medium uppercase tracking-wider border mt-1.5",
                              ROLE_COLORS[userRole!]
                            )}
                          >
                            {userRole}
                          </span>
                        </div>
                      </Link>

                      <div className="border-t border-border" />

                      {/* Actions */}
                      <div className="p-2 space-y-0.5">
                        {auth.current_admin && (
                          <Link
                            href="/admin/dashboard"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-accent rounded-lg transition-colors group"
                          >
                            <LayoutDashboard className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="font-medium">Dashboard</p>
                              <p className="text-xs text-muted-foreground">
                                Manage events &amp; drivers
                              </p>
                            </div>
                          </Link>
                        )}
                        {(auth.current_admin || auth.current_organizer) && (
                          <Link
                            href={
                              auth.current_admin
                                ? "/admin/championships"
                                : "/organizer/championships"
                            }
                            onClick={closeAll}
                            className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-accent rounded-lg transition-colors group"
                          >
                            <Trophy className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="font-medium">
                                {auth.current_organizer
                                  ? "My Championships"
                                  : "Championships"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Standings &amp; series
                              </p>
                            </div>
                          </Link>
                        )}
                      </div>

                      <div className="border-t border-border" />

                      <div className="p-2">
                        <button
                          onClick={() => handleLogout(getLogoutPath())}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                        >
                          <LogOut className="h-5 w-5" />
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="p-3 space-y-4">
                      {/* Driver */}
                      <div>
                        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Driver
                        </p>
                        <div className="space-y-0.5">
                          <Link
                            href="/login"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-3 py-3 hover:bg-accent rounded-lg transition-colors group"
                          >
                            <LogIn className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="text-sm font-medium">Login</p>
                              <p className="text-xs text-muted-foreground">
                                View your profile &amp; races
                              </p>
                            </div>
                          </Link>
                          <Link
                            href="/signup"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-3 py-3 hover:bg-accent rounded-lg transition-colors group"
                          >
                            <UserPlus className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="text-sm font-medium">Sign Up</p>
                              <p className="text-xs text-muted-foreground">
                                Create account to race
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>

                      <div className="border-t border-border" />

                      {/* Organiser section */}
                      <div>
                        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Organiser
                        </p>
                        <div className="space-y-0.5">
                          <Link
                            href="/organizer/login"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-3 py-3 hover:bg-accent rounded-lg transition-colors group"
                          >
                            <LogIn className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="text-sm font-medium">Login</p>
                              <p className="text-xs text-muted-foreground">
                                Manage events &amp; registrations
                              </p>
                            </div>
                          </Link>
                          <Link
                            href="/organizer/signup"
                            onClick={closeAll}
                            className="flex items-center gap-3 px-3 py-3 hover:bg-accent rounded-lg transition-colors group"
                          >
                            <UserPlus className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                            <div>
                              <p className="text-sm font-medium">Sign Up</p>
                              <p className="text-xs text-muted-foreground">
                                Create &amp; manage racing events
                              </p>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hamburger — mobile */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-300 md:hidden"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
              >
                <Menu
                  className={cn(
                    "h-5 w-5 absolute transition-all duration-300",
                    menuOpen ? "opacity-0 rotate-90" : "opacity-100 rotate-0"
                  )}
                />
                <X
                  className={cn(
                    "h-5 w-5 absolute transition-all duration-300",
                    menuOpen ? "opacity-100 rotate-0" : "opacity-0 -rotate-90"
                  )}
                />
              </button>
            </div>
          </nav>
        </header>
      </div>

      {/* ───── Mobile Menu ───── */}
      <div
        className={cn(
          "fixed inset-0 z-40 md:hidden transition-all duration-300",
          menuOpen ? "visible" : "invisible pointer-events-none"
        )}
      >
        {/* Backdrop */}
        <div
          className={cn(
            "absolute inset-0 bg-background/60 backdrop-blur-sm transition-opacity duration-300",
            menuOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setMenuOpen(false)}
        />
        {/* Panel */}
        <div
          className={cn(
            "absolute top-16 right-0 bottom-0 w-4/5 max-w-xs bg-background border-l border-border shadow-2xl transition-transform duration-300 ease-out flex flex-col",
            menuOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="flex-1 overflow-y-auto p-6">
            <ul className="space-y-1">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      "flex items-center px-3 py-3 text-base font-medium rounded-lg transition-colors",
                      isActive(link.href)
                        ? "text-foreground bg-accent"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {currentUser && (
              <div className="mt-6 pt-6 border-t border-border space-y-1">
                <Link
                  href={getProfileLink()}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <img
                    src={avatarUrl || defaultAvatar}
                    alt=""
                    className="h-9 w-9 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {getUserName()}
                    </p>
                    <span
                      className={cn(
                        "inline-flex items-center px-1.5 py-0.5 mt-0.5 rounded text-[10px] font-medium uppercase tracking-wide border",
                        ROLE_COLORS[userRole!]
                      )}
                    >
                      {userRole}
                    </span>
                  </div>
                </Link>

                {auth.current_admin && (
                  <Link
                    href="/admin/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                )}

                {(auth.current_admin || auth.current_organizer) && (
                  <Link
                    href={
                      auth.current_admin
                        ? "/admin/championships"
                        : "/organizer/championships"
                    }
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <Trophy className="h-4 w-4" />
                    {auth.current_organizer
                      ? "My Championships"
                      : "Championships"}
                  </Link>
                )}

                <button
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout(getLogoutPath());
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}

            {!currentUser && (
              <div className="mt-6 pt-6 border-t border-border space-y-4">
                <div>
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Driver
                  </p>
                  <Link
                    href="/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <LogIn className="h-4 w-4" />
                    Login
                  </Link>
                  <Link
                    href="/signup"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    Sign Up
                  </Link>
                </div>
                <div>
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Organiser
                  </p>
                  <Link
                    href="/organizer/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <LogIn className="h-4 w-4" />
                    Login
                  </Link>
                  <Link
                    href="/organizer/signup"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-accent/50 rounded-lg transition-colors"
                  >
                    <UserPlus className="h-4 w-4" />
                    Sign Up
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ───── Search Overlay ───── */}
      <div
        className={cn(
          "fixed inset-0 z-50 transition-all duration-300",
          searchOpen ? "visible" : "invisible pointer-events-none"
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-background/80 backdrop-blur-2xl transition-opacity duration-300",
            searchOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setSearchOpen(false)}
        />
        <div
          className={cn(
            "relative flex items-start justify-center pt-[18vh] px-4 transition-all duration-300",
            searchOpen
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-4"
          )}
        >
          <div className="w-full max-w-lg">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="What are you looking for?"
                className="w-full h-14 pl-12 pr-12 rounded-2xl border border-border bg-background text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ───── Main Content ───── */}
      <main className="container mx-auto px-4 py-8 relative z-10">{children}</main>

      {/* ───── Footer ───── */}
      <footer className="border-t border-border mt-12">
        <div className="container mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} RaceFlow</span>
          <div className="flex items-center gap-6">
            <Link
              href="/about"
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <Info className="w-4 h-4" />
              About
            </Link>
            <Link
              href="/terms"
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <ScrollText className="w-4 h-4" />
              Terms
            </Link>
            <Link
              href="/privacy"
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <ShieldCheck className="w-4 h-4" />
              Privacy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BaseLayout;
